import { useMemo, useState } from 'react'
import PageHeader from '../components/PageHeader.jsx'
import { useChampionships } from '../features/command-center/useChampionships.js'
import { auditChampionships } from '../lib/championshipAudit.js'
import DefectList from '../features/integrity/DefectList.jsx'
import { allItems, byState } from '../lib/championshipGaps.js'
import { api } from '../lib/api.js'
import { useAccess } from '../lib/access.jsx'
import { ACTIONS } from '../lib/rbac.js'
import styles from './DataIntegrity.module.css'

/**
 * CHAMPIONSHIP RECORD INTEGRITY — the check that keeps a state fixed.
 *
 * Brian, after the Texas/UIL audit: "this needs to be fully fixed and done
 * durable… this is one state. just imagine all the rest of the states."
 *
 * He was right that Texas was not a Texas problem. Measured across all 13,393
 * records, only THREE of 65 states were clean: 3,489 with no championship key,
 * 2,580 with no event date (invisible in every date-driven view a rep works),
 * 403 duplicates, 123 with no sport, and 61 state-level records with no state.
 *
 * ⚠️ IT READS THE SAME FEED THE BOARD DOES. No second endpoint and no cached
 * snapshot — `useChampionships` already pages the full universe for the Command
 * Center, so this is live by construction and cannot disagree with the board.
 * The rules live in `lib/championshipAudit.js` as pure functions with their own
 * verify suite; this page only draws them. Two copies of a normalizer have
 * already drifted apart in this codebase once and broken a live surface.
 *
 * ⚠️ CLEAN IS NOT COMPLETE, AND THE PAGE SAYS SO. Every structural defect here
 * is findable from the records alone. Two of the five Texas problems were not:
 * swimming held six meets UIL does not crown, and Military Marching Band was
 * absent entirely. Only a governing body's official list answers that — the
 * `state-championship-authority` skill — so a green row means "nothing
 * structurally wrong", never "we have them all". Letting green imply complete
 * would be the same false reassurance this page exists to remove.
 */

const nf = (n) => (typeof n === 'number' ? n.toLocaleString() : '—')

export default function DataIntegrity() {
  const { can, why } = useAccess()
  const allowed = can(ACTIONS.CC_VIEW)
  const { data, status, loadedCount, complete } = useChampionships()
  const [open, setOpen] = useState('')
  /* ⚠️ ACQUISITION IS A WRITE. Dry run and real run are separate buttons, and
   * the load is idempotent by sr_championship_key — pressing it twice creates
   * nothing the second time. */
  const canRun = can(ACTIONS.SYSTEM_RUN)
  const [acq, setAcq] = useState({ busy: false, result: null, error: '', dry: null, done: 0, total: 0 })

  /* ⚠️ THE 30-SECOND GATEWAY CEILING. `loadChampionships` does one HubSpot
   * lookup PER ITEM to stay idempotent, so 205 items in one request is a
   * guaranteed 504 — measured, not predicted. Worse, the gateway gives up while
   * the SERVER KEEPS GOING, so a single big request reports failure while
   * records are still being created. That exact shape already burned us on the
   * championship splitter.
   *
   * So it goes in batches, SEQUENTIALLY, each comfortably inside the window.
   * Idempotency by sr_championship_key is what makes this safe: a batch that
   * times out anyway is simply re-run and creates nothing twice. */
  const BATCH = 25

  const acquire = async (dryRun) => {
    const items = allItems()
    setAcq({ busy: true, error: '', result: null, dry: null, done: 0, total: items.length })
    const tally = { total: 0, created: 0, skipped: 0, failed: 0, errors: [] }
    try {
      for (let i = 0; i < items.length; i += BATCH) {
        const r = await api.championshipsLoad(items.slice(i, i + BATCH), { dryRun })
        tally.total += r.total || 0
        tally.created += r.created || 0
        tally.skipped += r.skipped || 0
        tally.failed += r.failed || 0
        if (r.errors?.length) tally.errors.push(...r.errors.slice(0, 3))
        setAcq((a) => ({ ...a, done: Math.min(i + BATCH, items.length) }))
      }
      setAcq({ busy: false, error: '', done: items.length, total: items.length,
        result: dryRun ? null : tally, dry: dryRun ? tally : null })
    } catch (e) {
      /* Report what DID land before the failure rather than a bare error — the
       * batches already committed are real records. */
      setAcq({ busy: false, result: dryRun ? null : tally, dry: dryRun ? tally : null,
        done: 0, total: items.length,
        error: `${e?.body?.detail || e?.body?.error || e?.message || 'Load failed'} — ${tally.created} created before this point.` })
    }
  }

  const audit = useMemo(() => auditChampionships(data || []), [data])
  /* Which defect the worklist below is showing, and optionally scoped to one
     state. Null means the page is still just the report. */
  const [drill, setDrill] = useState(null)
  const t = audit.totals

  if (!allowed) {
    return (
      <>
        <PageHeader eyebrow="Data" title="Championship Integrity" />
        <div className={`card ${styles.section}`}>
          <p>{why?.(ACTIONS.CC_VIEW) || 'You do not have permission to view this.'}</p>
        </div>
      </>
    )
  }

  return (
    <>
      <PageHeader
        eyebrow="Data"
        title="Championship Integrity"
        subtitle="Every state, checked for the defects that make a record unusable — missing keys and dates, duplicates, and a class recorded alongside its own divisions."
      />

      <section className={`card ${styles.section}`}>
        {status === 'loading' && (
          <p className={styles.muted}>Reading the full universe — {nf(loadedCount)} records so far.</p>
        )}
        {/* ⚠️ A partial load must never be reported as a finished audit: a state
            whose records have not arrived yet would read as clean. */}
        {status !== 'loading' && !complete && (
          <p className={styles.bad}>Only {nf(loadedCount)} records loaded — this is a partial audit, not a full one.</p>
        )}

        {/* ⚠️ A COUNT YOU CANNOT OPEN IS A REPORT, NOT A TOOL.
          * These were nine static numbers. Brian: "There isnt a call to action.
          * It doesn't allow me to see the issues or to review or change them."
          * The four that name a defect are now buttons into the worklist; the
          * three that are context (clean, overlaps, total) stay inert, because
          * a control that does nothing is worse than plain text. */}
        <div className={styles.grid}>
          <div><b data-bad={t.statesWithIssues ? '1' : '0'}>{nf(t.statesWithIssues)}</b><span>states with issues</span></div>
          <div><b>{nf(t.statesClean)}</b><span>structurally clean</span></div>
          <button type="button" className={styles.kpi} onClick={() => setDrill({ defect: 'noKey', state: null })}>
            <b data-bad={t.noKey ? '1' : '0'}>{nf(t.noKey)}</b><span>no championship key</span>
          </button>
          <button type="button" className={styles.kpi} onClick={() => setDrill({ defect: 'noDate', state: null })}>
            <b data-bad={t.noDate ? '1' : '0'}>{nf(t.noDate)}</b><span>no event date</span>
          </button>
          <div><b data-bad={t.duplicateExtra ? '1' : '0'}>{nf(t.duplicateExtra)}</b><span>duplicate records</span></div>
          <div><b data-bad={t.overlaps ? '1' : '0'}>{nf(t.overlaps)}</b><span>class / division overlaps</span></div>
          <button type="button" className={styles.kpi} onClick={() => setDrill({ defect: 'noSport', state: null })}>
            <b data-bad={t.noSport ? '1' : '0'}>{nf(t.noSport)}</b><span>no sport</span>
          </button>
          <button type="button" className={styles.kpi} onClick={() => setDrill({ defect: 'noState', state: null })}>
            <b data-bad={t.noStateOnStateRecord ? '1' : '0'}>{nf(t.noStateOnStateRecord)}</b><span>state record, no state</span>
          </button>
          <div><b>{nf(t.records)}</b><span>records audited</span></div>
        </div>

        <p className={styles.muted}>
          <b>Tap a number to see the records.</b> Every defect opens the list behind it, with the
          fix where one honestly exists — and a plain statement where it does not.
        </p>

        {drill && (
          <DefectList
            records={data || []}
            defect={drill.defect}
            state={drill.state}
            onClose={() => setDrill(null)}
          />
        )}

        <p className={styles.muted}>
          A missing date is not cosmetic — the record falls into NO DATE and disappears from Stages,
          Worklist, Calendar and every countdown, which is where a rep actually works. An overlap
          means a bare class is recorded beside its own divisions, so somebody is working a record
          that can never have a single winner.
        </p>
        <p className={styles.note}>
          <b>Structurally clean is not complete.</b> These checks find what is wrong with the records
          we hold; they cannot find a championship we never created. Texas passed on swimming while
          holding six meets UIL does not crown, and while Military Marching Band was missing
          entirely. Only the governing body&rsquo;s official list settles that, one state at a time.
        </p>
      </section>

      {/* ---- ACQUIRE ---- */}
      <section className={`card ${styles.section}`}>
        <h2 className={styles.h}>Acquire the missing championships</h2>
        <p className={styles.muted}>
          {allItems().length} championships across {byState().length} states, each read off the
          governing body&rsquo;s own published list and counted rather than inferred. These are
          overwhelmingly the fine-arts, academic and spirit titles — one-act play, literary, debate,
          speech, scholastic bowl, chess, esports, dance, spiritline. Every one crowns a winner who
          buys recognition.
        </p>
        <p className={styles.note}>
          <b>Nothing here is a guess.</b> Where the research could not establish a division list —
          Arizona&rsquo;s Spiritline sizes, Minnesota&rsquo;s Speech roster, Oklahoma&rsquo;s esports
          titles — the group is deliberately absent rather than estimated. A championship we invent
          is worse than one we are missing: a rep works it and waits for a winner who is never
          crowned. Ratings-only events (state music festivals, Virginia robotics) are excluded for
          the same reason. No event date is invented either.
        </p>

        <div className={styles.grid}>
          {byState().slice(0, 12).map((s) => (
            <div key={s.st}><b>{nf(s.titles)}</b><span>{s.state}</span></div>
          ))}
        </div>

        {canRun && (
          <div className={styles.actions}>
            <button className="btn" type="button" disabled={acq.busy} onClick={() => acquire(true)}>
              {acq.busy ? `Working ${acq.done} of ${acq.total}…` : 'Dry run — write nothing'}
            </button>
            <button className="btn btn-primary" type="button" disabled={acq.busy} onClick={() => acquire(false)}>
              Create them in HubSpot
            </button>
          </div>
        )}
        {acq.error && <p className={styles.bad}>{acq.error}</p>}
        {acq.dry && (
          <p className={styles.muted}>
            Dry run: {nf(acq.dry.total)} checked · <b>{nf(acq.dry.created)} would be created</b> ·
            {' '}{nf(acq.dry.skipped)} already exist · {nf(acq.dry.failed)} failed.
          </p>
        )}
        {acq.result && (
          <p className={styles.muted}>
            <b>{nf(acq.result.created)} created</b> · {nf(acq.result.skipped)} already existed ·
            {' '}{nf(acq.result.failed)} failed.
            {acq.result.errors?.length > 0 && <> First errors: {acq.result.errors.slice(0, 3).join(' · ')}</>}
          </p>
        )}
      </section>

      <section className={`card ${styles.section}`}>
        <h2 className={styles.h}>By state · worst first</h2>
        {/* ⚠️ TEN COLUMNS IN 390px. Measured on the shipped bundle: this table
            was still side by side at phone width inside an `overflow-x`
            scroller with the state pinned, so a reader saw the state name, one
            count, and nine columns off the right edge — on the page whose
            whole job is to show WHICH defect a state has. `cc-cards` stacks it
            into a labelled card per state.

            ⚠️ THE ROW HEADER IS A <td>, NOT A <th>, AND THAT IS DELIBERATE.
            cc-mobile.css identifies a data row by `tr:has(> td[data-col=name])`
            and a <th> does not match it, so the state cell would have stayed
            a table header inside a card and the row would never have stacked.
            `role="rowheader"` keeps the semantics a screen reader needs. */}
        <div className={`${styles.scroll} cc-scrollcue cc-cards-wrap`}>
          <table className={`${styles.table} cc-cards`}>
            <thead>
              <tr>
                {/* Every non-zero defect count in this table is a link into the
                  * records behind it. A table of numbers with no way in is what
                  * made this page unusable. */}
                <th className={styles.sticky}>State</th>
                <th>Records</th><th>No key</th><th>No date</th><th>No sport</th>
                <th>No state</th><th>Duplicates</th><th>Overlaps</th><th>Issues</th><th />
              </tr>
            </thead>
            <tbody>
              {audit.states.map((s) => (
                <tr key={s.state} data-clean={s.clean ? '1' : '0'}>
                  <td role="rowheader" className={styles.sticky} data-col="name" data-label="State">{s.state}</td>
                  <td data-label="Records">{nf(s.records)}</td>
                  <td data-label="No key" data-zero={!s.noKey ? '1' : '0'}>{s.noKey
                    ? <button type="button" className={styles.cellLink} onClick={() => setDrill({ defect: 'noKey', state: s.state })}>{nf(s.noKey)}</button>
                    : nf(s.noKey)}</td>
                  <td data-label="No date" data-zero={!s.noDate ? '1' : '0'}>{s.noDate
                    ? <button type="button" className={styles.cellLink} onClick={() => setDrill({ defect: 'noDate', state: s.state })}>{nf(s.noDate)}</button>
                    : nf(s.noDate)}</td>
                  <td data-label="No sport" data-zero={!s.noSport ? '1' : '0'}>{s.noSport
                    ? <button type="button" className={styles.cellLink} onClick={() => setDrill({ defect: 'noSport', state: s.state })}>{nf(s.noSport)}</button>
                    : nf(s.noSport)}</td>
                  <td data-label="No state" data-zero={!s.noStateOnStateRecord ? '1' : '0'}>{s.noStateOnStateRecord
                    ? <button type="button" className={styles.cellLink} onClick={() => setDrill({ defect: 'noState', state: s.state })}>{nf(s.noStateOnStateRecord)}</button>
                    : nf(s.noStateOnStateRecord)}</td>
                  <td data-label="Duplicates" data-zero={!s.duplicateExtra ? '1' : '0'}>{nf(s.duplicateExtra)}</td>
                  <td data-label="Overlaps" data-zero={!s.overlaps ? '1' : '0'}>{nf(s.overlaps)}</td>
                  <td data-label="Issues"><b>{nf(s.issues)}</b></td>
                  <td data-col="cta">
                    {(s.duplicateSample.length > 0 || s.overlapSample.length > 0) && (
                      <button className="btn" type="button"
                        onClick={() => setOpen(open === s.state ? '' : s.state)}>
                        {open === s.state ? 'Hide' : 'Show'}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* The named offenders, so a fix starts from a record id rather than a count. */}
        {open && (() => {
          const s = audit.states.find((x) => x.state === open)
          if (!s) return null
          return (
            <div className={styles.detail}>
              <h3 className={styles.h}>{s.state}</h3>
              {s.duplicateSample.length > 0 && (
                <>
                  <p className={styles.muted}>Recorded more than once in the same season:</p>
                  <ul className={styles.list}>
                    {s.duplicateSample.map((d, i) => (
                      <li key={i}>{d.name} <span className={styles.muted}>· {d.season} · {d.ids.length} records ({d.ids.join(', ')})</span></li>
                    ))}
                  </ul>
                </>
              )}
              {s.overlapSample.length > 0 && (
                <>
                  <p className={styles.muted}>A bare class recorded beside its own divisions:</p>
                  <ul className={styles.list}>
                    {s.overlapSample.map((o, i) => (
                      <li key={i}>{o.parent} <span className={styles.muted}>· also exists as {o.children.join(' and ')}</span></li>
                    ))}
                  </ul>
                </>
              )}
            </div>
          )
        })()}
      </section>
    </>
  )
}
